import { getKeyIdentifierFieldNameFromFields } from './fieldSetKeyIdentifier'
import { getFieldSetOverview } from '~/services/fieldSets'
import type { TEntityId } from '~/types/models'
import type { TMSService } from '~/types/msService/models'

const mn = '[cynqueModules/fieldSetKeyIdentifierCache.ts]'

/**
 * fieldSet id -> the `name` of the field it flags as its key identifier.
 *
 * WHY A CACHE AT ALL
 * ------------------
 * `product.fieldSet` arrives as an IEntitySelectItem -- `{id, label}` and
 * nothing else (`select_entities.field_sets` server-side) -- so the flagged
 * field's name has to be fetched. `MSService()` needs it once per msService
 * row, and an overview can carry a thousand of them.
 *
 * The saving is per **fieldSet**, not per product and certainly not per row:
 * 21 of the 55 mock products share fieldSet `XXFS0002` (DIA), and the entire
 * staging database holds 4 field sets. So the ceiling on requests is the number
 * of fieldSets that exist, and after the first overview of a session it is
 * usually zero.
 *
 * ⚠ TWO MAPS, AND THE PROMISE ONE IS THE POINT.
 * `inFlight` caches the PROMISE, not the result. Caching only the result would
 * still let a sweep fire one request per row before the first response landed;
 * handing every caller the same pending promise collapses them to one request.
 * `resolved` exists because `MSService()` is synchronous and cannot await --
 * see `peekKeyIdentifierFieldName`.
 */
const inFlight = new Map<TEntityId, Promise<string | undefined>>()
const resolved = new Map<TEntityId, string | undefined>()

/**
 * The distinct fieldSet ids these msServices' products point at.
 *
 * Products without a fieldSet contribute nothing -- they can have no key
 * identifier, which is the whole rule.
 */
export function collectFieldSetIds(
  msServices: TMSService[] | undefined
): TEntityId[] {
  if (!msServices) {
    return []
  }
  const ids = new Set<TEntityId>()
  for (const msService of msServices) {
    const fieldSetId = msService.product?.fieldSet?.id
    if (fieldSetId) {
      ids.add(fieldSetId)
    }
  }
  return Array.from(ids)
}

/**
 * Fetch and cache the key-identifier field name for each of these fieldSets.
 *
 * Call this ONCE, before a sweep that will run `MSService()` -- the silo
 * overview factory does it in `asyncData`, which lands before `created()` runs
 * the propagation that calls `setUpMSServices`. The ids are fetched in
 * parallel, so N fieldSets cost one round trip rather than N.
 */
export async function primeFieldSetKeyIdentifierFieldNames(
  fieldSetIds: TEntityId[]
): Promise<void> {
  const fn = `${mn} ${primeFieldSetKeyIdentifierFieldNames.name} |`
  if (!fieldSetIds.length) {
    return
  }
  console.debug(`${fn} priming ${fieldSetIds.length} fieldSet(s)`, fieldSetIds)
  await Promise.all(fieldSetIds.map(_getKeyIdentifierFieldName))
}

/** `collectFieldSetIds` + `primeFieldSetKeyIdentifierFieldNames`, in one call. */
export async function primeFieldSetKeyIdentifiersForMSServices(
  msServices: TMSService[] | undefined
): Promise<void> {
  await primeFieldSetKeyIdentifierFieldNames(collectFieldSetIds(msServices))
}

/**
 * The cached key-identifier field name for a fieldSet, WITHOUT fetching.
 *
 * Synchronous on purpose. `MSService()` runs inside a `forEach` over every
 * msService in an overview and is called from `Object.assign` expressions all
 * over the tree; making it async would mean making all of those async.
 *
 * Returns undefined when the cache is cold, which a caller must treat as "I
 * don't know", NOT as "this msService has no key identifier" -- see the
 * no-clobber guard in `cynqueModules/dataObjects/MSService.ts`.
 */
export function peekKeyIdentifierFieldName(
  fieldSetId: TEntityId | undefined
): string | undefined {
  if (!fieldSetId) {
    return undefined
  }
  return resolved.get(fieldSetId)
}

/**
 * Drop a fieldSet's cached answer, so the next prime re-reads it.
 *
 * Called when the flag moves to a different field --
 * `pages/Field Sets/[id].vue`. Without it, editing a fieldSet and then visiting
 * an overview in the same session would read the pre-edit field name.
 */
export function invalidateFieldSetKeyIdentifier(fieldSetId: TEntityId): void {
  inFlight.delete(fieldSetId)
  resolved.delete(fieldSetId)
}

/** Test seam. Not for application code. */
export function _clearFieldSetKeyIdentifierCache(): void {
  inFlight.clear()
  resolved.clear()
}

function _getKeyIdentifierFieldName(
  fieldSetId: TEntityId
): Promise<string | undefined> {
  const fn = `${mn} _getKeyIdentifierFieldName |`
  const pending = inFlight.get(fieldSetId)
  if (pending) {
    return pending
  }
  const request = getFieldSetOverview(fieldSetId)
    .then((fieldSetOverview) => {
      const fieldName = getKeyIdentifierFieldNameFromFields(
        fieldSetOverview?.fields
      )
      resolved.set(fieldSetId, fieldName)
      console.debug(
        `${fn} fieldSet '${fieldSetId}' key identifier field: ${
          fieldName ?? '(none)'
        }`
      )
      return fieldName
    })
    .catch((error) => {
      // A fieldSet a product still points at can be gone (404), and a fetch can
      // simply fail. Neither is worth breaking an overview over: the cost is a
      // blank Key Identifier cell.
      //
      // ⚠ The failure is deliberately NOT cached -- `inFlight` is cleared so a
      // later prime retries. A cached failure would cost nothing on a genuinely
      // deleted fieldSet but would silently blank the column for the rest of
      // the session after one transient blip, which is the worse of the two.
      console.error(
        `${fn} couldn't read fieldSet '${fieldSetId}' overview:`,
        error
      )
      inFlight.delete(fieldSetId)
      return undefined
    })
  inFlight.set(fieldSetId, request)
  return request
}
